import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { TooltipComponent } from '../components/tooltip/tooltip.component';

@Directive({
  selector: '[appTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  private element = inject(ElementRef);
  private renderer = inject(Renderer2);
  private viewContainerRef = inject(ViewContainerRef);

  @Input() appTooltip: string | TemplateRef<unknown> = '';
  @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';
  @Input() tooltipOffset: number = 8;
  @Input() tooltipDelay: number = 150;

  private tooltipComponent: ComponentRef<TooltipComponent> | null = null;
  private showTimeout: NodeJS.Timeout | null = null;
  private hideTimeout: NodeJS.Timeout | null = null;

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent): void {
    this.clearTimeouts();

    if (!this.appTooltip) {
      return;
    }

    this.showTimeout = setTimeout(() => {
      this.showTooltip(event);
    }, this.tooltipDelay);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.clearTimeouts();

    this.hideTimeout = setTimeout(() => {
      this.hideTooltip();
    }, 100); // Small delay to allow moving to tooltip
  }

  private showTooltip(_event: MouseEvent): void {
    if (this.tooltipComponent) {
      // If the tooltip element is no longer in the DOM, clean up the stale reference
      if (!this.tooltipComponent.location.nativeElement.isConnected) {
        this.tooltipComponent = null;
      } else {
        return;
      }
    }

    // Create the tooltip component
    this.tooltipComponent =
      this.viewContainerRef.createComponent(TooltipComponent);

    // Set the content
    if (typeof this.appTooltip === 'string') {
      this.tooltipComponent.instance.content = this.appTooltip;
    } else {
      this.tooltipComponent.instance.template = this.appTooltip;
    }

    // Add event listeners to keep tooltip visible when hovering over it
    const tooltipElement = this.tooltipComponent.location.nativeElement;
    this.renderer.listen(tooltipElement, 'mouseenter', () => {
      this.clearTimeouts();
    });

    this.renderer.listen(tooltipElement, 'mouseleave', () => {
      this.hideTooltip();
    });

    // Append to body first so dimensions are measurable, then position
    this.renderer.appendChild(document.body, tooltipElement);
    this.positionTooltip();
  }

  private positionTooltip(): void {
    if (!this.tooltipComponent) {
      return;
    }

    const tooltipElement = this.tooltipComponent.location.nativeElement;
    const hostRect = this.element.nativeElement.getBoundingClientRect();
    const tooltipRect = tooltipElement.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    let top = 0;
    let left = 0;
    let actualPosition = this.tooltipPosition;

    // Calculate initial position based on preferred position
    switch (this.tooltipPosition) {
      case 'top':
        top = hostRect.top - tooltipRect.height - this.tooltipOffset;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = hostRect.bottom + this.tooltipOffset - 40;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.left - tooltipRect.width - this.tooltipOffset;
        break;
      case 'right':
        top = hostRect.top + (hostRect.height - tooltipRect.height) / 2;
        left = hostRect.right + this.tooltipOffset;
        break;
    }

    // Adjust if tooltip would go outside window boundaries
    // Check horizontal boundaries
    if (left < 0) {
      if (this.tooltipPosition === 'left') {
        // Switch to right side
        left = hostRect.right + this.tooltipOffset;
        actualPosition = 'right';
      } else {
        left = this.tooltipOffset;
      }
    } else if (left + tooltipRect.width > windowWidth) {
      if (this.tooltipPosition === 'right') {
        // Switch to left side
        left = hostRect.left - tooltipRect.width - this.tooltipOffset;
        actualPosition = 'left';
      } else {
        left = windowWidth - tooltipRect.width - this.tooltipOffset;
      }
    }

    // Check vertical boundaries
    if (top < 0) {
      if (this.tooltipPosition === 'top') {
        // Switch to bottom
        top = hostRect.bottom + this.tooltipOffset;
        actualPosition = 'bottom';
      } else {
        top = this.tooltipOffset;
      }
    } else if (top + tooltipRect.height > windowHeight) {
      if (this.tooltipPosition === 'bottom') {
        // Switch to top
        top = hostRect.top - tooltipRect.height - this.tooltipOffset;
        actualPosition = 'top';
      } else {
        top = windowHeight - tooltipRect.height - this.tooltipOffset;
      }
    }

    // Apply position
    this.renderer.setStyle(tooltipElement, 'position', 'fixed');
    this.renderer.setStyle(tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(tooltipElement, 'left', `${left}px`);
    this.renderer.setStyle(tooltipElement, 'z-index', '9999');
    this.renderer.setStyle(tooltipElement, 'max-width', `${windowWidth - 2 * this.tooltipOffset}px`);

    // Update the tooltip's position class for arrow positioning
    this.tooltipComponent.instance.position = actualPosition;
  }

  private hideTooltip(): void {
    if (this.tooltipComponent) {
      const ref = this.tooltipComponent;
      const el = ref.location.nativeElement;
      // Null reference first so showTooltip() is never blocked if destroy() throws
      this.tooltipComponent = null;
      try {
        ref.destroy();
      } catch {
        // destroy() can throw when the element was moved to document.body
      }
      el.parentNode?.removeChild(el);
    }
  }

  private clearTimeouts(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    this.hideTooltip();
  }
}
